import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, throwError } from 'rxjs';
import { retry, tap } from 'rxjs/operators';
import { ModalDialogRetryComponent } from '@domgen/dgx-components';

/**
 *  @param maxRetryCount - Number of attempts to try before failing the request, defaults to 2
 *  @param dialogTimeout - How long to wait when the retry dialog appears, defaults to 5s
 *  @param componentToUseForDialog - Pass in the component to show when dialog appears, defaults to retry dialog
 *  @param logicToRunOnAttempt - Will run each time the passed in method is called, useful for loading states etc.
 *  @param logicToRunOnError - Will run when the request fails, useful for clearing down loading states etc.
 */
export interface RetryOnErrorConfig {
  maxRetryCount: number;
  dialogTimeout: number;
  componentToUseForDialog: Component;
  conditionToRetryRequest: (error: HttpErrorResponse) => boolean;
  logicToRunOnAttempt: () => void;
  logicToRunOnError: (error: HttpErrorResponse) => void;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorRetryService {
  constructor(private dialog: MatDialog) {}

  private openDialog(
    componentToUse = ModalDialogRetryComponent,
    countDown = 5000
  ): Observable<boolean> {
    return new Observable((observer) => {
      const dialogReference = this.dialog.open(componentToUse as any, {
        data: {
          timer: countDown,
        },
        autoFocus: 'dgx-components-button button',
      });

      dialogReference.afterClosed().subscribe({
        next: (outcome: { retry: boolean }) => {
          observer.next(outcome.retry);
        },
      });
    });
  }

  retryOnError<T>(
    methodToRetry: Observable<T>,
    config: Partial<RetryOnErrorConfig> = {}
  ): Observable<T> {
    if (config.logicToRunOnAttempt) {
      config.logicToRunOnAttempt();
    }

    return methodToRetry.pipe(
      retry({
        count: config.maxRetryCount ?? 2,
        delay: (error) => {
          if (config.logicToRunOnError) {
            config.logicToRunOnError(error);
          }

          if (
            config.conditionToRetryRequest &&
            !config.conditionToRetryRequest(error)
          ) {
            return throwError(() => error);
          }

          return this.openDialog(
            config.componentToUseForDialog as any,
            config.dialogTimeout
          ).pipe(
            tap((retry) => {
              if (retry && config.logicToRunOnAttempt) {
                config.logicToRunOnAttempt();
              }
            })
          );
        },
      })
    );
  }
}
